import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Plus, Trash2, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import WalkCard from '../components/WalkCard';
import LoginPrompt from '../components/LoginPrompt';
import { getUserLists, createList, deleteList, addWalkToList } from '../data/mock';
import { useAuth } from '../contexts/AuthContext';
import { getFreshToken } from '../lib/firebase';
import { useToast } from '../hooks/use-toast';

const FavoritesPage = () => {
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [lists, setLists] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [activeTab, setActiveTab] = useState('favorites');
  const [showSyncPrompt, setShowSyncPrompt] = useState(false);
  const [walksCache, setWalksCache] = useState({}); // { walkId: walkObj }

  // Sync backend favorites into localStorage on mount
  useEffect(() => {
    (async () => {
      try {
        const token = await getFreshToken();
        if (!token) return;
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const resp = await fetch(`${backendUrl}/api/favorites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          const backendIds = data.walk_ids || [];
          // Merge backend favorites into localStorage "favorites" list
          backendIds.forEach(id => addWalkToList(id, 'favorites'));
        }
      } catch (e) {
        console.error('Failed to sync backend favorites:', e);
      }
      refreshLists();
    })();
  }, []);

  useEffect(() => {
    refreshLists();
  }, [refreshKey]);

  // Fetch real walk data from the API for all walk IDs across all lists
  useEffect(() => {
    const allIds = [...new Set(Object.values(lists).flatMap(l => l.walks || []))];
    const missing = allIds.filter(id => !walksCache[id]);
    if (missing.length === 0) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    Promise.all(
      missing.map(id =>
        fetch(`${backendUrl}/api/walks/${id}`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )
    ).then(results => {
      const newCache = { ...walksCache };
      results.forEach(w => {
        if (!w) return;
        // Normalize to the shape WalkCard expects
        const distMiles = w.distance_meters ? (w.distance_meters / 1609.34).toFixed(1) : null;
        newCache[w.id] = {
          id: w.id,
          slug: w.slug,
          country: w.country || 'us',
          city: w.city || 'los-angeles',
          neighborhood: w.neighborhood || '',
          title: w.title,
          description: w.description,
          practicalSummary: w.practical_summary || w.description,
          distance: distMiles ? `${distMiles} miles` : null,
          duration: w.duration_minutes ? `${w.duration_minutes} min` : null,
          difficulty: w.difficulty,
          category: w.categories?.[0] || 'Urban Walk',
          region: w.region,
          photos: [w.main_image, ...(w.images || [])].filter(Boolean),
          main_image: w.main_image,
          userRatings: w.user_ratings || 0,
          favoriteCount: w.favorite_count || 0,
          completionCount: w.completion_count || 0,
        };
      });
      setWalksCache(newCache);
    });
  }, [lists]);

  // Show sync prompt if user has 3+ lists and isn't authenticated
  useEffect(() => {
    if (!isAuthenticated && Object.keys(lists).length >= 3) {
      setShowSyncPrompt(true);
    }
  }, [lists, isAuthenticated]);

  const refreshLists = () => {
    const userLists = getUserLists();
    setLists(userLists);
    
    // If active tab doesn't exist, switch to favorites
    if (!userLists[activeTab]) {
      setActiveTab('favorites');
    }
  };

  const handleCreateList = () => {
    if (!newListName.trim()) return;
    
    try {
      const newList = createList(newListName.trim(), newListDescription.trim());
      setNewListName('');
      setNewListDescription('');
      setShowCreateForm(false);
      refreshLists();
      setActiveTab(newList.id);
      
      toast({
        title: "List created",
        description: `"${newListName}" list has been created`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteList = (listId) => {
    if (deleteList(listId)) {
      refreshLists();
      setActiveTab('favorites');
      toast({
        title: "List deleted",
        description: "List has been removed",
      });
    }
  };

  const handleFavoriteToggle = () => {
    setRefreshKey(prev => prev + 1);
  };

  const getWalksForList = (listId) => {
    const list = lists[listId];
    if (!list) return [];
    return list.walks.map(id => walksCache[id]).filter(Boolean);
  };

  const totalWalks = Object.values(lists).reduce((acc, list) => acc + list.walks.length, 0);
  const listArray = Object.values(lists).sort((a, b) => {
    if (a.isDefault) return -1;
    if (b.isDefault) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-1">My Walk Lists</h1>
              <p className="text-gray-300">
                {totalWalks} walk{totalWalks !== 1 ? 's' : ''} saved in {Object.keys(lists).length} list{Object.keys(lists).length !== 1 ? 's' : ''}
              </p>
            </div>

            <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  New List
                </Button>
              </DialogTrigger>
              
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Walk List</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="listName">List Name *</Label>
                    <Input
                      id="listName"
                      placeholder="e.g., Morning Loops, First Walk of the Month"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      maxLength={50}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="listDescription">Description (optional)</Label>
                    <Textarea
                      id="listDescription"
                      placeholder="What kind of walks will you save here?"
                      value={newListDescription}
                      onChange={(e) => setNewListDescription(e.target.value)}
                      rows={2}
                      maxLength={200}
                    />
                  </div>
                  
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCreateList}
                      disabled={!newListName.trim()}
                    >
                      Create List
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        {/* Sync Prompt for Guest Users */}
        {showSyncPrompt && (
          <div className="mb-8">
            <LoginPrompt 
              trigger="syncLists"
              onDismiss={() => setShowSyncPrompt(false)}
            />
          </div>
        )}
        {Object.keys(lists).length === 0 ? (
          <div className="text-center py-20">
            <Heart className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">No Lists Yet</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Create your first list to start organizing your favorite walks. 
              Group them by mood, time of day, or any way that makes sense to you.
            </p>
            <Button onClick={() => setShowCreateForm(true)} size="lg">
              <Plus className="mr-2 h-4 w-4" />
              Create Your First List
            </Button>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full max-w-4xl mx-auto mb-8" style={{ gridTemplateColumns: `repeat(${Math.min(listArray.length, 5)}, 1fr)` }}>
              {listArray.map((list) => (
                <TabsTrigger key={list.id} value={list.id} className="flex items-center gap-2 px-4 py-2 data-[state=active]:!bg-amber-500 data-[state=active]:!text-white data-[state=active]:shadow-sm">
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">{list.name}</span>
                  <Badge variant="secondary" className={`text-xs ${activeTab === list.id ? 'bg-white/20 text-white' : ''}`}>
                    {list.walks.length}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            {listArray.map((list) => {
              const walksInList = getWalksForList(list.id);
              
              return (
                <TabsContent key={list.id} value={list.id} className="space-y-6">
                  {/* List Header */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {list.isDefault ? (
                              <Heart className="h-5 w-5 text-amber-500" />
                            ) : (
                              <List className="h-5 w-5 text-amber-500" />
                            )}
                            {list.name}
                          </CardTitle>
                          {list.description && (
                            <p className="text-gray-600 mt-1">{list.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>{list.walks.length} walk{list.walks.length !== 1 ? 's' : ''}</span>
                            <span>Created {new Date(list.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        
                        {!list.isDefault && (
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteList(list.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Walks Grid */}
                  {walksInList.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
                      <List className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No walks in this list yet</h3>
                      <p className="text-gray-600 mb-6">
                        Start adding walks from the walk pages or browse our collection
                      </p>
                      <Link to="/walks">
                        <Button>
                          Browse Walks
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {walksInList.map((walk) => (
                        <WalkCard 
                          key={`${list.id}-${walk.id}-${refreshKey}`} 
                          walk={walk} 
                          onFavoriteToggle={handleFavoriteToggle}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </section>

      {/* Floating Actions */}
      <FloatingActions />
      
      <Footer />
    </div>
  );
};

export default FavoritesPage;